import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './css/index.module.scss';

const ELLIPSIS = '…';

// Keeps the start and the end of the text and puts "…" in the middle, as many characters as fit the width.
// measure returns the width the page really renders for a string, so the result never overflows.
const fitText = (text, width, measure) => {
  const chars = Array.from(text);
  const build = (count) => {
    const startCount = Math.ceil(count / 2);
    const endCount = count - startCount;
    return `${chars.slice(0, startCount).join('').trimEnd()}${ELLIPSIS}${chars.slice(chars.length - endCount).join('').trimStart()}`;
  };

  let low = 0;
  let high = chars.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (measure(build(middle)) <= width) low = middle;
    else high = middle - 1;
  }

  return build(low);
};

// One-line text cut in the middle to the width its place allows. The full text is in the tooltip.
// The hidden copy keeps the full text width as the element's natural width, so it grows back when there is room.
function MiddleEllipsisText({
  text, className, as: Tag = 'p', ...props
}) {
  const fullText = text == null ? '' : String(text);
  const ref = useRef(null);
  const sizerRef = useRef(null);
  const measurerRef = useRef(null);
  const [displayText, setDisplayText] = useState(fullText);

  useLayoutEffect(() => {
    const element = ref.current;
    const sizer = sizerRef.current;
    const measurer = measurerRef.current;
    if (!element || !sizer || !measurer) return undefined;

    // Same font as the visible text, so the widths match exactly.
    const measure = (value) => {
      measurer.textContent = value;
      return measurer.getBoundingClientRect().width;
    };

    let isActive = true;
    const update = () => {
      if (!isActive) return;
      const style = getComputedStyle(element);
      // Fractional widths: when the element is as wide as its text, rounded widths would cut a text that fits.
      const width = element.getBoundingClientRect().width
        - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
        - parseFloat(style.borderLeftWidth) - parseFloat(style.borderRightWidth);
      const fullWidth = sizer.getBoundingClientRect().width;

      if (width <= 0 || fullWidth <= width + 0.5) setDisplayText(fullText);
      else setDisplayText(fitText(fullText, width, measure));
      measurer.textContent = '';
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    // Widths measured with a fallback font are wrong once the real font loads.
    document.fonts?.ready.then(update);

    return () => {
      isActive = false;
      observer.disconnect();
    };
  }, [fullText]);

  return (
    <Tag ref={ref} className={classNames(styles.middleEllipsisText, className)} title={fullText} {...props}>
      <span ref={sizerRef} className={styles.sizer} aria-hidden="true">{fullText}</span>
      <span ref={measurerRef} className={styles.sizer} aria-hidden="true" />
      {displayText}
    </Tag>
  );
}

export default MiddleEllipsisText;
